import { LoaderCircle, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateChat } from '@services/chat/mutations/useUpdateChat'
import { useChat } from '@services/chat/queries/useChat'

import { ChatConversationList } from '../components/widget/ChatConversationList'
import { ChatLauncher } from '../components/widget/ChatLauncher'
import { ChatPanel } from '../components/widget/ChatPanel'
import { ChatThread } from '../components/widget/ChatThread'
import { NewConversation } from '../components/widget/NewConversation'
import { useChatWidgetContext } from '../context'

export function ChatWidget({ user_id }: { user_id?: string } = {}) {
  const { t } = useTranslation('chat')
  const {
    isChatWidgetOpen,
    setIsChatWidgetOpen,
    hasNewMessages,
    activeChat,
    setActiveChat,
    saveBarVisible
  } = useChatWidgetContext()
  const updateChat = useUpdateChat()

  const [creating, setCreating] = useState(false)

  const chat = useChat(activeChat ?? '')
  const inThread = !!activeChat && !creating

  const openWidget = () => {
    setActiveChat(undefined)
    setCreating(false)
    setIsChatWidgetOpen(true)
  }

  let title = t('form.common.chats')
  let onBack: (() => void) | undefined
  if (creating) {
    title = t('form.common.create_chat')
    onBack = () => setCreating(false)
  } else if (inThread) {
    title = chat.data?.title || t('form.common.chat')
    onBack = () => setActiveChat(undefined)
  }

  return (
    <>
      {!isChatWidgetOpen && (
        <ChatLauncher
          hasUnread={hasNewMessages}
          onClick={openWidget}
          mobileHidden={saveBarVisible}
        />
      )}

      <ChatPanel
        open={isChatWidgetOpen}
        onClose={() => setIsChatWidgetOpen(false)}
        title={title}
        onBack={onBack}
        editableTitle={
          inThread && activeChat
            ? {
                value: chat.data?.title || '',
                onSave: (value) =>
                  updateChat.mutate({ id: activeChat, body: { title: value } })
              }
            : undefined
        }
        headerExtra={
          !creating && !inThread ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              aria-label={t('form.common.create_chat')}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-transform hover:scale-105"
            >
              <Plus className="h-5 w-5" />
            </button>
          ) : undefined
        }
      >
        {creating ? (
          <NewConversation
            user={user_id}
            onCreated={(id) => {
              setCreating(false)
              setActiveChat(id)
            }}
          />
        ) : inThread ? (
          chat.data ? (
            <ChatThread data={chat.data} />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
            </div>
          )
        ) : (
          <ChatConversationList
            user={user_id}
            onSelect={(id) => setActiveChat(id)}
          />
        )}
      </ChatPanel>
    </>
  )
}
